import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { useTranslation } from '@fluxora/i18n';
import { Alert, Button, EmptyState, Label, ListboxSelect, Spinner } from '@fluxora/ui';

import { usePlansQuery, usePricePointsForPlans } from '../api/plans.queries';
import { FunnelBackLink } from '../components/FunnelBackLink';
import { MeterPlanCard } from '../components/MeterPlanCard';
import {
  DEFAULT_PLAN_SORT,
  PLAN_SORT_IDS,
  isPlanSortId,
  sortPlans,
  type PlanSortId,
} from '../services/planOrdering';
import { filterPlansByTdu } from '../services/zone.service';
import { useFunnel } from '../state/FunnelProvider';

/**
 * SHOP-5A — the meter-filtered tariff shelf. Runs AFTER the meter + consumption
 * are resolved (the meter-first flow's "Shop plans" CTA lands here at
 * `/shop/plans`). It is separate from the legacy plan-first `/plans` + StartGate
 * (SHOP-6 removes that later).
 *
 * ── FILTER ─────────────────────────────────────────────────────────────────
 * Reads `{ meterDetail, monthlyUsageKwh }` from `FunnelProvider`, filters the
 * catalogue by the meter's real TDU CODE (`meterDetail.tduCode`, which the ESI ID
 * lookup returns alongside the display name — so the SPA never reconciles a name
 * back to a code), and prices each plan at the chosen monthly usage.
 *
 * ── GUARDS & GATES ─────────────────────────────────────────────────────────
 *   - no meter / no usage → redirect to the landing chooser.
 *   - BUSINESS meter → "no plans for your meter yet" (residential-only today, C).
 *   - all 5 async states for the plan list.
 *
 * ── ORDER ──────────────────────────────────────────────────────────────────
 * Price ascending by default, at the shopper's chosen usage. The shelf used to
 * render `/bff/plans` in whatever order it arrived — effectively
 * `termMonths ASC, price ASC`, which put the two MOST EXPENSIVE plans first
 * (QA 2026-08-05). Nothing here decides the rule: `planOrdering.ts` owns it,
 * including which figure is sorted on and why.
 */
export function MeterPlansPage(): JSX.Element {
  const { t } = useTranslation();
  const funnel = useFunnel();
  const meter = funnel.meterDetail;
  const monthly = funnel.monthlyUsageKwh;

  // Ask the BFF for THIS UTILITY only. It narrows before it prices, so the
  // tariffs belonging to the other five utilities are never costed — measured at
  // 7.75s for all 44 Texas ids against 4.46s for the 20 ONCR carries.
  //
  // ⚠️ GATED ON THE METER, and it has to be. Hooks all run BEFORE the guards
  // below, so on a render with no resolved meter — a deep link to this route, or
  // a refresh before the funnel rehydrates — an ungated `usePlansQuery(undefined)`
  // would fire the ALL-TEXAS fetch under the `'*'` key and pay for all 44 tariffs
  // on its way to redirecting away. That is precisely the cost this change exists
  // to remove, reintroduced on the one path nobody looks at.
  const plansQuery = usePlansQuery(meter?.tduCode, { enabled: meter !== undefined });
  const [sort, setSort] = useState<PlanSortId>(DEFAULT_PLAN_SORT);

  // ⚠️ EVERY HOOK RUNS BEFORE THE GUARDS BELOW. `useMemo`/`usePricePointsForPlans`
  // must not sit after the `<Navigate>` early return, or hook order breaks the
  // moment a meter resolves.
  // ⚠️ THE SERVER ALREADY NARROWED THIS. The filter is kept DELIBERATELY, as a
  // guard rather than as the mechanism, and the trade is worth stating:
  //
  //   · If the BFF filter regresses (or an older BFF is deployed under a newer
  //     SPA), keeping this means the shopper still sees only their own utility's
  //     plans and we merely pay to price the rest — a performance regression.
  //   · Removing it would mean the shopper is SHOWN plans their meter cannot
  //     take. That is the E-SF19 P1 defect QA already caught once, where an Oncor
  //     plan was featured with CenterPoint confirmed on the same screen.
  //
  // A silent slowdown is the better failure, so this stays. What stops it MASKING
  // the server filter is that the guarantee is pinned where it belongs: the query
  // layer asserts the request actually carried `?tdu=`
  // (`plans.queries.test.ts`), so a server filter that stopped being ASKED FOR
  // fails a test rather than being quietly absorbed here.
  //
  // It also still does real work on the render before the meter resolves.
  const tduPlans = useMemo(
    () =>
      plansQuery.data === undefined || meter === undefined
        ? []
        : filterPlansByTdu(plansQuery.data.plans, meter.tduCode),
    [plansQuery.data, meter],
  );

  // The same price points the cards render, so the shelf can order by the figure
  // the customer actually sees. Shares the cards' cache keys — no extra requests.
  const pricePoints = usePricePointsForPlans(
    useMemo(() => tduPlans.map((plan) => plan.tariffId), [tduPlans]),
  );

  const visiblePlans = useMemo(
    () => sortPlans(tduPlans, { sort, monthlyKwh: monthly ?? 0, pricePoints }),
    [tduPlans, sort, monthly, pricePoints],
  );

  // Guard: the meter-first flow must have resolved a meter + a usage first.
  if (meter === undefined || monthly === undefined) {
    return <Navigate to="/" replace />;
  }

  const contextPill =
    'inline-flex items-center rounded-full border border-border bg-surface px-3 py-1 text-xs font-semibold text-foreground shadow-sm';
  const header = (
    <header className="flex flex-col items-center gap-3 text-center">
      <h1 className="font-display text-3xl font-bold tracking-tight text-foreground">
        {t('shop.meterShelf.title')}
      </h1>
      {/* The context reads as a sentence whose FACTS are pills — the utility
          and the usage are the two things worth picking out at a glance. */}
      <p className="flex flex-wrap items-center justify-center gap-2 text-sm text-foreground/70">
        <span>{t('shop.meterShelf.context.showing')}</span>
        <span className={contextPill}>{meter.tdsp}</span>
        <span>{t('shop.meterShelf.context.pricedAt')}</span>
        <span className={contextPill}>{t('shop.meterShelf.context.usage', { kwh: monthly })}</span>
      </p>
    </header>
  );

  // Business meter → residential-only catalogue has nothing for it (decision C).
  if (meter.customerType === 'BUSINESS') {
    return (
      <main className="mx-auto flex max-w-6xl flex-col gap-6 px-4 py-8">
        <BackToStart label={t('shop.meterShelf.back')} />
        {header}
        <EmptyState
          title={t('shop.meterShelf.business.title')}
          description={t('shop.meterShelf.business.body')}
        />
      </main>
    );
  }

  /**
   * The shopper can re-sort. Price ascending is the DEFAULT, not the only order —
   * a default that steers has to be a default the customer can step out of, and
   * the compliance question about non-price ordering is still open.
   */
  const sortControl = (
    <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-end">
      <Label htmlFor="shelf-sort" className="text-sm text-foreground/70">
        {t('shop.meterShelf.sort.label')}
      </Label>
      {/* The COMMON listbox — our open panel, not the OS menu. */}
      <div className="w-full sm:w-72">
        <ListboxSelect
          id="shelf-sort"
          value={sort}
          onChange={(next) => {
            // Narrowed, never cast: an unrecognised id falls back to the
            // default rather than putting an unknown value into state.
            setSort(isPlanSortId(next) ? next : DEFAULT_PLAN_SORT);
          }}
          options={PLAN_SORT_IDS.map((id) => ({
            value: id,
            label: t(`shop.meterShelf.sort.${id}`),
          }))}
        />
      </div>
    </div>
  );

  return (
    <main className="mx-auto flex max-w-6xl flex-col gap-6 px-4 py-8">
      {header}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <BackToStart label={t('shop.meterShelf.back')} />
        {/* Hidden while there is nothing to order — an empty shelf with a
            sort dropdown reads as a broken filter. */}
        {plansQuery.isSuccess && visiblePlans.length > 0 ? sortControl : null}
      </div>

      {plansQuery.isPending ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" label={t('shop.meterShelf.loading')} />
        </div>
      ) : plansQuery.isError ? (
        <Alert variant="danger" title={t('shop.meterShelf.error.title')} role="alert">
          <div className="flex flex-col items-start gap-3">
            <span>{t('shop.meterShelf.error.body')}</span>
            <Button variant="secondary" size="sm" onClick={() => void plansQuery.refetch()}>
              {t('shop.meterShelf.retry')}
            </Button>
          </div>
        </Alert>
      ) : visiblePlans.length === 0 ? (
        <EmptyState
          title={t('shop.meterShelf.empty.title')}
          description={t('shop.meterShelf.empty.body', { tdu: meter.tdsp })}
        />
      ) : (
        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {visiblePlans.map((plan) => (
            <MeterPlanCard key={plan.tariffId} plan={plan} monthlyKwh={monthly} />
          ))}
        </section>
      )}

    </main>
  );
}

/**
 * The shelf's back affordance goes to the meter chooser, which is the funnel's start
 * — so it is the same control as every other step, not a bespoke one.
 */
function BackToStart({ label }: { readonly label: string }): JSX.Element {
  return <FunnelBackLink to="/" label={label} />;
}
